<template>
  <div v-if="progress" class="operation-progress" role="status" :title="position">
    <span class="steps" aria-hidden="true">
      <span v-for="index in stepCount" :key="index" :class="['step', stepClass(index)]"></span>
    </span>
    <span class="label">{{ label }}</span>
    <span v-if="queueText" class="count queue">{{ queueText }}</span>
    <span v-if="hasCount" class="count">{{ countText }}</span>
    <span class="sr-only">{{ position }}</span>
  </div>
</template>

<script>
// Where a long-running operation is: the step it is in, among all of them, and — when the step has items to
// count, as the import of the secondary market certificates does — how many are done.
// Fed by the progress the operation endpoint reports (see utils/operationPoller.js); knows nothing of how
// it is obtained. The names of the steps are those of the kind of operation (operations.<kind>.steps.<Step>
// in the locales). No inline styles (the CSP forbids them): the steps are marked with classes.
export default {
  props: {
    // { status, step, stepIndex, stepCount, done, total, errorCode } or null: nothing to show.
    progress: {
      type: Object,
      default: null,
    },
    // Which operation it is: the key of its steps in the locales.
    kind: {
      type: String,
      default: 'efficiencyTestExecution',
    },
  },

  computed: {
    stepCount() {
      return this.progress ? Math.max(0, this.progress.stepCount || 0) : 0
    },

    label() {
      const key = `operations.${this.kind}.steps.${this.progress.step}`
      return this.$te(key) ? this.$t(key) : this.progress.step
    },

    hasCount() {
      return this.progress.done !== null && this.progress.done !== undefined && !!this.progress.total
    },

    countText() {
      const params = { done: this.progress.done, total: this.progress.total }
      const key = `operations.${this.kind}.counts.${this.progress.step}${this.progress.done === 0 ? 'None' : ''}`
      const fallback = `operations.${this.kind}.counts.${this.progress.step}`
      if (this.$te(key)) {
        return this.$t(key, params)
      }
      return this.$te(fallback) ? this.$t(fallback, params) : this.$t('operations.count', params)
    },

    // Only the step that waits on DOXA carries estimatedWaitSeconds (a lower bound), null when no certificate of
    // the test is in progress. How many are ahead in the queue is not shown: it says nothing to the user.
    queueText() {
      const p = this.progress
      if (!p.queueState || !(p.estimatedWaitSeconds > 0)) {
        return ''
      }
      return this.$t(`operations.${this.kind}.queue.estimate`, { minutes: Math.max(1, Math.round(p.estimatedWaitSeconds / 60)) })
    },

    position() {
      return this.$t('operations.position', { index: this.progress.stepIndex, count: this.progress.stepCount })
    },
  },

  methods: {
    stepClass(index) {
      if (index < this.progress.stepIndex) {
        return 'step-done'
      }
      return index === this.progress.stepIndex ? 'step-current' : 'step-pending'
    },
  },
}
</script>

<style lang="scss" scoped>
.operation-progress {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: 0.875rem;
}

.steps {
  display: inline-flex;
  margin-right: 0.5rem;
}

.step {
  width: 8px;
  height: 8px;
  margin-right: 3px;
  border-radius: 50%;
  border: 1px solid rgb(40, 167, 69);
  background: transparent;
}

.step-done {
  background: rgb(40, 167, 69);
}

.step-current {
  background: rgb(255, 193, 7);
  border-color: rgb(255, 193, 7);
}

.label {
  margin-right: 0.25rem;
}

.count {
  color: rgb(108, 117, 125);
}
</style>
